const UST_STATIONS_URL = "https://api.ust.is/aq/a/getStations";
const UST_LATEST_URL = "https://api.ust.is/aq/a/getLatest";
// Our own relay (functions/index.js) for stations api.ust.is leaves out
const EXTRA_STATIONS_URL = "/api/extra-stations";
const VEDUR_STATIONS_URL = "https://api.vedur.is/weather/stations";
const VEDUR_LATEST_URL =
  "https://api.vedur.is/weather/observations/aws/10min/latest";
const VEDUR_HOURLY_URL = "https://api.vedur.is/weather/observations/aws/hour";

// api.vedur.is intermittently answers 502 without CORS headers, which the
// browser reports as a network error, so retry a couple of times.
const getJson = async (url, attempts = 3) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${url} responded ${response.status}`);
    return await response.json();
  } catch (error) {
    if (attempts <= 1) throw error;
    await new Promise((resolve) => setTimeout(resolve, 800));
    return getJson(url, attempts - 1);
  }
};

export const distanceKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Stations currently reporting to getLatest, with coordinates from getStations,
// plus the relay's stations. A relay record replaces the api.ust.is station it
// names in `replaces`; if the relay is down, the UST data is used on its own.
export const fetchAirQuality = async () => {
  const [stations, latest, extra] = await Promise.all([
    getJson(UST_STATIONS_URL),
    getJson(UST_LATEST_URL),
    getJson(EXTRA_STATIONS_URL, 1).catch((error) => {
      console.warn("Extra stations unavailable:", error);
      return {};
    }),
  ]);
  const extraStations = Object.values(extra || {});
  const replaced = new Set(extraStations.map((s) => s.replaces));
  const coordsById = Object.fromEntries(
    stations.map((s) => [
      s.local_id,
      { lat: parseFloat(s.latitude), lon: parseFloat(s.longitude) },
    ]),
  );

  return Object.values(latest || {})
    .filter((station) => !replaced.has(station.local_id))
    .map((station) => ({ ...station, ...coordsById[station.local_id] }))
    .concat(extraStations)
    .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon));
};

// Station coordinates rarely change, so fetch them once per page load
let vedurStations;
const vedurStationsById = () => {
  vedurStations ??= getJson(VEDUR_STATIONS_URL)
    .then((stations) => Object.fromEntries(stations.map((s) => [s.station, s])))
    .catch((error) => {
      vedurStations = undefined;
      throw error;
    });
  return vedurStations;
};

// Observations that have both wind speed and direction, with coordinates
const toWeather = (observations, stationsById) =>
  observations
    .filter((o) => o.f !== null && o.d !== null && stationsById[o.station])
    .map((o) => ({
      id: o.station,
      name: o.name,
      time: o.time,
      lat: stationsById[o.station].lat,
      lon: stationsById[o.station].lon,
      speed: o.f, // m/s
      direction: o.d, // degrees the wind blows FROM
      directionText: o.d_txt,
      temperature: o.t,
    }));

// Latest 10-minute observations
export const fetchWeather = async () => {
  const [stationsById, observations] = await Promise.all([
    vedurStationsById(),
    getJson(VEDUR_LATEST_URL),
  ]);
  return toWeather(observations, stationsById);
};

// Today's hourly observations for every station currently reporting, grouped
// by hour: { [hour in ms since epoch]: weather[] }. One request covers all
// stations (about 2 MB), so this is only loaded when the timeline is used.
export const fetchWindToday = async () => {
  const [stationsById, latest] = await Promise.all([
    vedurStationsById(),
    getJson(VEDUR_LATEST_URL),
  ]);
  // Icelandic time is UTC, so the UTC date is today's date
  const day = new Date().toISOString().slice(0, 10);
  const ids = latest.map((o) => `station_id=${o.station}`).join("&");
  const observations = await getJson(
    `${VEDUR_HOURLY_URL}?${ids}&day_from=${day}&day_to=${day}`,
  );

  const byHour = {};
  toWeather(observations, stationsById).forEach((w) => {
    const hour = Date.parse(`${w.time}Z`);
    (byHour[hour] ??= []).push(w);
  });
  return byHour;
};
